import {
  AsyncProps,
  AsyncSelect,
  chakraComponents,
  ChakraStylesConfig,
  GroupBase,
  Props,
  Select,
  SelectInstance,
} from 'chakra-react-select';
import type { RefAttributes } from 'react';
import { forwardRef } from 'react';

type IsMulti = boolean;
type Option = unknown;
type Group = GroupBase<Option>;
type SelectComponent = Props<Option, IsMulti, Group> & RefAttributes<SelectInstance<Option, IsMulti, Group>>;
type AsyncSelectComponent = AsyncProps<Option, IsMulti, Group> & RefAttributes<SelectInstance<Option, IsMulti, Group>>;

const chakraStyles: ChakraStylesConfig<Option, IsMulti, Group> = {
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
  }),
  valueContainer: (provided) => ({
    ...provided,
    p: '0px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    bgColor: 'transparent',
    border: 'none',
    p: 'sm',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    borderColor: 'white',
  }),
  multiValue: (provided) => ({
    ...provided,
    border: '1px solid',
    borderColor: 'gray.300',
    boxShadow: 'none',
    color: 'black',
    fontWeight: 'normal',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    '> div[role="button"]': {
      width: 'md',
      height: 'md',
      _hover: {
        bgColor: 'transparent',
        '> svg': {
          fill: 'gray.500',
        },
      },
      _active: {
        bgColor: 'transparent',
      },
      _focus: {
        boxShadow: 'none',
      },
    },
  }),
  crossIcon: (provided) => ({
    ...provided,
    fill: 'gray.400',
    width: 'sm',
    height: 'sm',
    '> path': {
      fill: 'inherit',
    },
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    '> svg': {
      fill: 'gray.400',
      '> path': {
        fill: 'inherit',
      },
    },
  }),
};

export const ChakraSelect = forwardRef<unknown, Omit<SelectComponent, 'ref'>>((props, ref) => (
  <Select
    tagVariant='outline'
    selectedOptionStyle='check'
    chakraStyles={{ ...chakraStyles }}
    ref={ref as SelectComponent['ref']}
    {...props}
  />
));
ChakraSelect.displayName = 'ChakraSelect';

export const ChakraAsyncSelect = forwardRef<unknown, Omit<AsyncSelectComponent, 'ref'>>((props, ref) => (
  <AsyncSelect
    tagVariant='outline'
    selectedOptionStyle='check'
    chakraStyles={{ ...chakraStyles }}
    components={{
      LoadingIndicator: (indicatorProps) => (
        <chakraComponents.LoadingIndicator
          color='red.500'
          emptyColor='transparent'
          spinnerSize='md'
          speed='0.45s'
          thickness='2px'
          {...indicatorProps}
        />
      ),
    }}
    ref={ref as AsyncSelectComponent['ref']}
    {...props}
  />
));
ChakraAsyncSelect.displayName = 'ChakraAsyncSelect';

export * from './format-options-array';
